import React, { useState } from 'react';
import { useAddClienteMutation } from '../api/clientesApiSlice';
import type { ClientePayload } from '../api/clientesApiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../auth/authSlice';
import { useGetEmpresaByIdQuery } from '../../empresas/api/empresasApiSlice';

const camposObligatorios = [
  'apellido', 'nombre'
];

const initialState: Partial<ClientePayload> = {
  documento: '',
  telefono: '',
  apellido: '',
  nombre: '',
  direccion: '',
  localidad: '',
  provincia: '',
  pais: '',
  empresa_id: undefined,
};

interface CampoTexto {
  num: number;
  label: string;
}

const ClienteForm: React.FC = () => {
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  // Tomar empresa_id y nombre de la empresa seleccionada en el perfil
  const empresaId = currentUser?.empresa_id || undefined;
  const { data: empresaApi, isLoading: loadingEmpresa } = useGetEmpresaByIdQuery(empresaId, { skip: !empresaId });
  const empresa = empresaApi;
  console.log('Empresa en ClienteForm:', empresa);
  
  // Obtener etiquetas personalizadas para los campos de texto
  const camposTextoLabels: CampoTexto[] = [
    { num: 1, label: empresa?.ntcliente1 || `Texto 1` },
    { num: 2, label: empresa?.ntcliente2 || `Texto 2` },
    { num: 3, label: empresa?.ntcliente3 || `Texto 3` },
    { num: 4, label: empresa?.ntcliente4 || `Texto 4` },
    { num: 5, label: empresa?.ntcliente5 || `Texto 5` },
  ];
  
  const [form, setForm] = useState<Partial<ClientePayload>>({ ...initialState, empresa_id: empresaId });
  const [addCliente, { isLoading, isSuccess, isError, error }] = useAddClienteMutation();
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [noEmpresaError, setNoEmpresaError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validar = () => {
    const errs: Record<string, string> = {};
    camposObligatorios.forEach((campo) => {
      if (!form[campo as keyof ClientePayload]) {
        errs[campo] = 'Campo obligatorio';
      }
    });
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNoEmpresaError(false);
    if (!empresaId) {
      setNoEmpresaError(true);
      return;
    }
    const errs = validar();
    setErrores(errs);
    if (Object.keys(errs).length > 0) return;
    try {
      // Limpiar el payload: solo enviar campos con valor (no undefined, no string vacía)
      const payload: Partial<ClientePayload> = { ...form, empresa_id: empresaId };
      Object.keys(payload).forEach((key) => {
        if (
          payload[key as keyof ClientePayload] === undefined ||
          payload[key as keyof ClientePayload] === ''
        ) {
          delete payload[key as keyof ClientePayload];
        }
      });
      await addCliente(payload as ClientePayload).unwrap();
      setForm({ ...initialState, empresa_id: empresaId });
      setErrores({});
    } catch (err) {
      if (err && typeof err === 'object' && 'data' in err && err.data && typeof err.data === 'object' && 'message' in err.data) {
        setErrores({ general: err.data.message as string });
      }
    }
  };

  // Extraer mensaje de error del backend si existe
  let backendErrorMsg = '';
  if (isError && error && typeof error === 'object' && 'data' in error) {
    backendErrorMsg = (error as any).data?.message || '';
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-card text-card-foreground p-6 rounded-lg shadow-md max-w-xl mx-auto mt-8 border border-border dark:bg-[#181A20] dark:text-white"
      style={{ boxShadow: '0 2px 16px 0 rgba(0,0,0,0.15)' }}
    >
      {/* Mostrar empresa seleccionada */}
      <div className="mb-4">
        <span className="block text-sm font-semibold text-muted-foreground">Empresa seleccionada:</span>
        {empresaId ? (
          <span className="block text-base font-bold text-foreground">{empresa?.nombre} (ID: {empresaId})</span>
        ) : (
          <span className="block text-destructive text-sm">No tienes una empresa asignada. Contacta a un administrador.</span>
        )}
      </div>
      {noEmpresaError && (
        <div className="mb-4 text-destructive text-sm">No tienes una empresa asignada. Contacta a un administrador.</div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Campos principales */}
        {['documento','telefono','apellido','nombre','direccion','localidad','provincia','pais'].map((campo) => (
          <div key={campo}>
            <label className="block text-sm font-medium text-foreground dark:text-gray-200 mb-1 capitalize" htmlFor={campo}>{campo}</label>
            <input
              type="text"
              id={campo}
              name={campo}
              value={form[campo as keyof ClientePayload] || ''}
              onChange={handleChange}
              className={`w-full rounded-md border px-3 py-2 bg-background dark:bg-[#23272F] border-border text-foreground dark:text-white focus:outline-none focus:ring-2 focus:ring-primary ${errores[campo] ? 'border-destructive' : ''}`}
              autoComplete="off"
            />
            {errores[campo] && <span className="text-destructive text-xs dark:text-red-400">{errores[campo]}</span>}
          </div>
        ))}
        {/* Empresa solo visible para superadmin, pero solo lectura */}
        {isSuperAdmin && (
          <div>
            <label className="block text-sm font-medium text-foreground dark:text-gray-200 mb-1" htmlFor="empresa_id">Empresa ID</label>
            <input
              type="number"
              id="empresa_id"
              name="empresa_id"
              value={empresaId || ''}
              readOnly
              className="w-full rounded-md border px-3 py-2 bg-background dark:bg-[#23272F] border-border text-foreground dark:text-white focus:outline-none focus:ring-2 focus:ring-primary opacity-60 cursor-not-allowed"
              autoComplete="off"
            />
          </div>
        )}
        
        {/* Campos de texto 1 a 5 con nombres personalizados */}
        {camposTextoLabels.map(({ num, label }) => (
          <div key={`ntcliente${num}`}>
            <label className="block text-sm font-medium text-foreground dark:text-gray-200 mb-1" htmlFor={`ntcliente${num}`}>{label}</label>
            <textarea
              id={`ntcliente${num}`}
              name={`ntcliente${num}`}
              value={form[`ntcliente${num}` as keyof ClientePayload] || ''}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-md border px-3 py-2 bg-background dark:bg-[#23272F] border-border text-foreground dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={`Ingrese ${label}`}
            />
          </div>
        ))}
      </div>
      
      {/* Errores generales y botón de envío */}
      {errores.general && (
        <div className="my-4 text-destructive text-sm">{errores.general}</div>
      )}
      {backendErrorMsg && (
        <div className="my-4 text-destructive text-sm">{backendErrorMsg}</div>
      )}
      {isSuccess && (
        <div className="my-4 text-success text-sm">Cliente agregado correctamente.</div>
      )}
      <div className="mt-6 flex justify-end">
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md shadow-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar Cliente'}
        </button>
      </div>
    </form>
  );
};

export default ClienteForm; 